import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { okrService } from "@/services/okrService";
import { KeyResultFilter, ICreateKeyResult, IUpdateKeyResult } from "@/types";

/** Canonical params for "all KRs of one objective" — shared so cache reads/writes hit the same key. */
export const krListParams = (objectivePid: string): KeyResultFilter =>
  ({ objectivePid, limit: 50, sortBy: "orderIndex", sortOrder: "ASC" });

export const useKeyResults = (params?: KeyResultFilter, enabled = true) => {
  return useQuery({
    queryKey: ["key-results", params],
    queryFn: () => okrService.getKeyResults(params),
    enabled,
  });
};

export const useKeyResult = (pid: string) => {
  return useQuery({
    queryKey: ["key-results", pid],
    queryFn: () => okrService.getKeyResult(pid),
    enabled: !!pid,
  });
};

export const useCreateKeyResult = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ICreateKeyResult) => okrService.createKeyResult(data),
    onSuccess: (kr) => {
      queryClient.invalidateQueries({ queryKey: ["key-results"] });
      queryClient.invalidateQueries({ queryKey: ["objectives"] });
      // PPP rows embed the KR chips they link to, so those lists go stale on every KR write.
      queryClient.invalidateQueries({ queryKey: ["ppps"] });
      queryClient.setQueryData(["key-results", kr.pid], kr);
    },
  });
};

export const useUpdateKeyResult = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pid, data }: { pid: string; data: IUpdateKeyResult }) =>
      okrService.updateKeyResult(pid, data),
    onSuccess: (kr) => {
      queryClient.invalidateQueries({ queryKey: ["key-results"] });
      queryClient.invalidateQueries({ queryKey: ["objectives"] });
      // PPP rows embed the KR chips they link to, so those lists go stale on every KR write.
      queryClient.invalidateQueries({ queryKey: ["ppps"] });
      queryClient.setQueryData(["key-results", kr.pid], kr);
    },
  });
};

export const useDeleteKeyResult = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pid: string) => okrService.deleteKeyResult(pid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["key-results"] });
      queryClient.invalidateQueries({ queryKey: ["objectives"] });
      // PPP rows embed the KR chips they link to, so those lists go stale on every KR write.
      queryClient.invalidateQueries({ queryKey: ["ppps"] });
    },
  });
};
